/*
 * Copyright 2026 Sony Corporation
 */

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';

import { useAuth } from '../../contexts/auth-context';

export interface GatewaySetupDialogProps {
  worldId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Downloading a bundle rotates the gateway's certificate and revokes the
 * previous one, so the warning is shown before the request, not after.
 */
export const GatewaySetupDialog = ({ worldId, open, onClose }: GatewaySetupDialogProps) => {
  const { api } = useAuth();
  const [name, setName] = useState('lab-01');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const download = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { blob, filename } = await api.createGatewayBundle(worldId, { name });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = filename;
      anchor.click();
      // The blob is held alive by the object URL until it is revoked; the click
      // has already started the download by the time this runs.
      URL.revokeObjectURL(href);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the bundle.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Download gateway config</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}
        <DialogContentText variant="body2" className="mb-4">
          The bundle contains a private key and is delivered once. Downloading again issues a new certificate and
          revokes the old one, so any gateway still using it will stop connecting.
        </DialogContentText>
        <TextField
          label="Gateway name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
          margin="dense"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={download} variant="contained" disabled={busy || name.trim() === ''}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
